export interface MoneyValue {
  /** Amount in minor units (e.g. cents). MUST be an integer. */
  readonly amount: number;
  /** ISO 4217 currency code. */
  readonly currency: string;
}

export interface FormatMoneyOptions {
  /**
   * Controls how the sign is displayed. Use 'accounting' to get parenthesised
   * negatives (e.g. ($15.00)). Note: 'accounting' is handled via currencySign,
   * not signDisplay, for compatibility with all Node.js / browser runtimes.
   */
  readonly signDisplay?: 'auto' | 'always' | 'never' | 'exceptZero' | 'accounting';
  readonly maximumFractionDigits?: number;
  readonly minimumFractionDigits?: number;
}

const MINOR_UNIT_DIGITS: Record<string, number> = {
  USD: 2,
  EUR: 2,
  GBP: 2,
  LBP: 0,
  JOD: 3,
  AED: 2,
  SAR: 2,
  EGP: 2,
};

function digitsFor(currency: string): number {
  return MINOR_UNIT_DIGITS[currency.toUpperCase()] ?? 2;
}

export function formatMoney(
  value: MoneyValue,
  locale: string,
  opts: FormatMoneyOptions = {},
): string {
  if (!Number.isInteger(value.amount)) {
    throw new TypeError(`formatMoney requires integer minor units, got ${String(value.amount)}`);
  }
  const fraction = digitsFor(value.currency);
  const major = value.amount / 10 ** fraction;

  const isAccounting = opts.signDisplay === 'accounting';

  // 'accounting' is not a valid signDisplay value in Intl.NumberFormat — it is
  // expressed via currencySign: 'accounting'. Only pass signDisplay for the
  // non-accounting variants to avoid a RangeError on any runtime.
  const fmtOpts: Intl.NumberFormatOptions = {
    style: 'currency',
    currency: value.currency,
    minimumFractionDigits: opts.minimumFractionDigits ?? fraction,
    maximumFractionDigits: opts.maximumFractionDigits ?? fraction,
    currencySign: isAccounting ? 'accounting' : 'standard',
    ...(opts.signDisplay !== undefined && !isAccounting ? { signDisplay: opts.signDisplay } : {}),
  };

  return new Intl.NumberFormat(locale, fmtOpts).format(major);
}
